// Event Emitter for typed event handling

export class EventEmitter<E> {
    private handlers: {
        [K in keyof E]?: Array<E[K]>;
    } = {};

    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor() {
        // Empty constructor needed for proper instantiation in tests
    }

    // Overload for single event
    on<K extends keyof E>(event: K, handler: E[K]): () => void;

    // Overload for multiple events
    on<K extends keyof E>(events: Array<K>, handler: E[K]): () => void;

    // Implementation
    on<K extends keyof E>(eventOrEvents: K | Array<K>, handler: E[K]): () => void {
        const events = Array.isArray(eventOrEvents) ? eventOrEvents : [eventOrEvents];

        events.forEach((event) => {
            if (!this.handlers[event]) this.handlers[event] = [];
            this.handlers[event]!.push(handler);
        });

        return () => {
            events.forEach((event) => {
                const index = this.handlers[event]?.indexOf(handler);
                if (index !== undefined && index >= 0) {
                    this.handlers[event]!.splice(index, 1);
                }
            });
        };
    }

    emit<K extends keyof E>(event: K, ...args: E[K] extends (...a: infer P) => unknown ? P : never): void {
        this.handlers[event]?.forEach((h) => {
            if (typeof h === "function") {
                (h as (...args: Array<unknown>) => void)(...args);
            }
        });
    }

    // Overload for single event
    off<K extends keyof E>(event: K, handler?: E[K]): void;

    // Overload for multiple events
    off<K extends keyof E>(events: Array<K>, handler?: E[K]): void;

    // Implementation
    off<K extends keyof E>(eventOrEvents: K | Array<K>, handler?: E[K]): void {
        const events = Array.isArray(eventOrEvents) ? eventOrEvents : [eventOrEvents];

        events.forEach((event) => {
            if (handler) {
                const index = this.handlers[event]?.indexOf(handler);
                if (index !== undefined && index >= 0) {
                    this.handlers[event]!.splice(index, 1);
                }
            } else {
                this.handlers[event] = [];
            }
        });
    }

    removeAllListeners(): void {
        this.handlers = {};
    }
}
